<template>
  <div class="container-fluid">
    <!-- 페이지 헤더 -->
    <div class="row">
      <div class="col-lg-12">
        <div class="d-flex flex-wrap align-items-center justify-content-center">
          <div class="text-center">
            <h4 class="mb-3">카테고리소싱</h4>
            <p class="mb-0">쿠팡 카테고리별 트래픽 정보와 TOP20 키워드를 분석합니다.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 컨트롤 영역 -->
    <div class="row mb-4">
      <div class="col-lg-12">
        <!-- 메인 버튼들 -->
        <div class="d-flex justify-content-center align-items-center mb-4" style="gap: 15px;">
          <!-- 카테고리그룹 선택 -->
          <b-dropdown
            id="category-group-dropdown"
            :text="'카테고리그룹: ' + activeGroup"
            class="mr-2"
            variant="outline-primary"
            :disabled="isLoading"
          >
            <b-dropdown-item
              v-for="group in categoryGroups"
              :key="group"
              :active="group === activeGroup"
              @click="switchCategoryGroup(group)"
            >
              <div class="d-flex justify-content-between align-items-center">
                <div>{{ group }}</div>
                <b-badge variant="secondary" v-if="group !== 'Default'" @click.stop="deleteCategoryGroup(group)">Del</b-badge>
              </div>
            </b-dropdown-item>
            <b-dropdown-divider v-if="categoryGroups.length > 0"></b-dropdown-divider>
            <b-dropdown-item @click="createCategoryGroup">새 카테고리그룹</b-dropdown-item>
          </b-dropdown>

          <!-- 카테고리선택 버튼 -->
          <button type="button" class="btn btn-info" @click="openCategoryModal" :disabled="isLoading">
            카테고리선택 ({{ selectedCategoryPath || '미선택' }})
          </button>

          <!-- 초기화 버튼 -->
          <button
            type="button"
            class="btn btn-secondary"
            @click="clearData"
            :disabled="isLoading"
          >
            초기화
          </button>
        </div>
      </div>
    </div>

    <!-- 진행률 표시 -->
    <div v-if="isLoading" class="row mb-4">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <div class="progress mb-2">
              <div
                class="progress-bar"
                :style="{ width: progressPercent + '%' }"
                role="progressbar"
              >
                {{ progressPercent.toFixed(1) }}%
              </div>
            </div>
            <small class="text-muted">{{ progressMessage }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- 결과 테이블 -->
    <div class="row" v-if="analysisResults.length > 0">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="card-title mb-0">카테고리 분석 결과</h5>
            <div>
              <button class="btn btn-success btn-sm" @click="downloadExcel">
                <i class="fas fa-download"></i> 엑셀 다운로드
              </button>
            </div>
          </div>
          <div class="card-body">
            <CDataTable
                storage-key="categorySourcing.1"
              :headers="tableHeaders"
              :items="analysisResults"
              :item-key="'id'"
              :loading="false"
              :pagination="true"
              :items-per-page="50"
              class="table-responsive"
            >
              <!-- 키워드 상세보기 슬롯 -->
              <template #keywordsDetail="{ item }">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  @click="showKeywordDetails(item)"
                  :disabled="!item.keywordList || item.keywordList.length === 0"
                >
                  <i class="fas fa-eye mr-1"></i>상세보기
                </button>
              </template>
            </CDataTable>
          </div>
        </div>
      </div>
    </div>

    <!-- 통계 정보 -->
    <div class="row mt-3" v-if="analysisResults.length > 0">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <div class="row text-center">
              <div class="col-md-3">
                <h5>{{ analysisResults.length }}</h5>
                <small class="text-muted">수집된 카테고리</small>
              </div>
              <div class="col-md-3">
                <h5>{{ totalImpressions.toLocaleString() }}</h5>
                <small class="text-muted">총 노출수</small>
              </div>
              <div class="col-md-3">
                <h5>{{ totalClicks.toLocaleString() }}</h5>
                <small class="text-muted">총 클릭수</small>
              </div>
              <div class="col-md-3">
                <h5>{{ lastCollectedAt }}</h5>
                <small class="text-muted">마지막 수집시간</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 카테고리 선택 모달 -->
    <div class="modal fade" :class="{ show: showCategoryModal }" :style="{ display: showCategoryModal ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-xl" style="max-height: 90vh;">
        <div class="modal-content" style="max-height: 90vh; overflow-y: auto;">
          <div class="modal-header">
            <h5 class="modal-title">카테고리 선택 - {{ activeGroup }}</h5>
            <button type="button" class="btn-close" @click="showCategoryModal = false"></button>
          </div>
          <div class="modal-body">
            <!-- 카테고리 로딩 스피너 -->
            <div v-if="!categoryTree && isLoadingCategories" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <p class="mt-3 text-muted">카테고리 리스트를 가져오는 중...</p>
            </div>

            <!-- 카테고리 선택 영역 -->
            <div class="row" v-if="categoryTree">
              <div
                v-for="(level, index) in categoryLevels"
                :key="index"
                :class="getColumnClass(categoryLevels.length)"
                class="mb-3"
                v-show="level.categories.length > 0"
              >
                <div class="card h-100">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">{{ index + 1 }}차 카테고리</h6>
                  </div>
                  <div class="card-body p-0" style="max-height: 400px; overflow-y: auto;">
                    <ul class="list-group list-group-flush">
                      <li
                        v-for="category in level.categories"
                        :key="category.displayItemCategoryDto.displayItemCategoryCode"
                        class="list-group-item cursor-pointer d-flex justify-content-between align-items-center"
                        :class="{
                          'active': category.displayItemCategoryDto.displayItemCategoryCode === level.selected,
                          'list-group-item-action': true
                        }"
                        @click="selectCategory(index, category)"
                      >
                        <span class="text-truncate">{{ category.displayItemCategoryDto.name }}</span>
                        <span v-if="category.child && category.child.length > 0" class="badge badge-secondary badge-pill">
                          {{ category.child.length }}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- 선택된 카테고리 정보 -->
            <div v-if="selectedCategory" class="mt-3">
              <div class="alert alert-info">
                <strong>선택된 카테고리:</strong> {{ selectedCategoryPath }}
                <br>
                <small>수집 대상: {{ leafCategoriesCount }}개 하위 카테고리</small>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div class="text-muted small">
                {{ selectedCategory ? `${leafCategoriesCount}개 카테고리 수집 예정` : '카테고리를 선택하세요' }}
              </div>
              <div>
                <button type="button" class="btn btn-secondary mr-2" @click="showCategoryModal = false">
                  <i class="fas fa-times mr-1"></i>닫기
                </button>
                <button type="button" class="btn btn-primary" @click="startDataCollection" :disabled="isLoading || !selectedCategory">
                  <i class="fas fa-play mr-1"></i>수집시작
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade" :class="{ show: showCategoryModal }" v-if="showCategoryModal"></div>

    <!-- 키워드 상세 모달 -->
    <div class="modal fade" :class="{ show: showKeywordModal }" :style="{ display: showKeywordModal ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">TOP20 키워드 상세 정보</h5>
            <button type="button" class="btn-close" @click="showKeywordModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedKeywordData">
              <div class="mb-3">
                <h6>카테고리: {{ selectedKeywordData.categoryPath }}</h6>
                <small class="text-muted">수집일시: {{ new Date(selectedKeywordData.collectedAt).toLocaleString() }}</small>
              </div>

              <!-- 키워드 데이터 테이블 -->
              <CDataTable
                  storage-key="categorySourcing.2"
                :headers="keywordTableHeaders"
                :items="keywordTableData"
                :item-key="'rank'"
                :loading="false"
                :pagination="true"
                :items-per-page="20"
                class="table-responsive"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showKeywordModal = false">
              <i class="fas fa-times mr-1"></i>닫기
            </button>
            <button type="button" class="btn btn-info" @click="addToSourcingBoard" v-if="selectedKeywordData">
              <i class="fas fa-clipboard-list mr-1"></i>소싱관리 담기
            </button>
            <button type="button" class="btn btn-success" @click="downloadKeywordData" v-if="selectedKeywordData">
              <i class="fas fa-download mr-1"></i>키워드 데이터 다운로드
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade" :class="{ show: showKeywordModal }" v-if="showKeywordModal"></div>
  </div>
</template>

<script>
import { scopedDbName } from '@/services/scopedDB'
import CDataTable from '@/components/common/CDataTable.vue'
import * as XLSX from 'xlsx'
import axios from 'axios'

export default {
  name: 'CategorySourcing',
  components: {
    CDataTable
  },
  data() {
    return {
      // 로딩 상태
      isLoading: false,
      isLoadingCategories: false,
      progressPercent: 0,
      progressMessage: '',

      // 카테고리 데이터
      categoryTree: null,
      categoryLevels: [], // 각 레벨별 카테고리 목록
      selectedCategory: null,
      selectedCategoryPath: '',
      leafCategoriesCount: 0,

      // 그룹 관리
      categoryGroups: ['Default'],
      activeGroup: 'Default',

      // 분석 결과
      analysisResults: [],
      tableHeaders: [],

      // IndexedDB
      db: null,
      dbName: 'CategorySourcingDB',
      storeName: 'categorySourcingData',

      // 모달 관련
      showCategoryModal: false,
      showKeywordModal: false,
      selectedKeywordData: null,
      keywordTableHeaders: [
        { text: '순위', value: 'rank', sortable: true },
        { text: '키워드', value: 'keyword', sortable: true },
        { text: '평균가', value: 'avgPrice', sortable: true },
        { text: '가격대', value: 'priceRange', sortable: false },
        { text: '총노출수', value: 'totalImpression', sortable: true },
        { text: '자연노출수', value: 'organicImpression', sortable: true },
        { text: '광고노출수', value: 'adsImpression', sortable: true },
        { text: '광고노출비율(%)', value: 'adsImpressionRate', sortable: true },
        { text: '검색수', value: 'searchVolume', sortable: true },
        { text: '검색클릭수', value: 'searchClick', sortable: true },
        { text: '클릭율(%)', value: 'clickRate', sortable: true }
      ],
      keywordTableData: []
    }
  },
  computed: {
    totalImpressions() {
      return this.analysisResults.reduce((sum, item) => sum + (item.totalImpression || 0), 0)
    },
    totalClicks() {
      return this.analysisResults.reduce((sum, item) => sum + (item.glanceView || 0), 0)
    },
    lastCollectedAt() {
      if (this.analysisResults.length === 0) return '-'
      const latest = Math.max(...this.analysisResults.map(item => new Date(item.collectedAt).getTime()))
      return new Date(latest).toLocaleString()
    }
  },
  async created() {
    // 카테고리그룹 초기화 (IndexedDB에서 기존 그룹들 로드)
    await this.initializeCategoryGroups()
  },
  async mounted() {
    console.log('🚀 [카테고리소싱] 컴포넌트 마운트 시작')

    // Extension 메시지 리스너 등록
    window.addEventListener('message', this.handleExtensionMessage)

    // IndexedDB 초기화
    await this.initializeDB()

    // 기존 데이터 로드
    await this.loadFromIndexedDB()

    // 테이블 헤더 초기화
    this.initializeTableHeaders()

    console.log('✅ [카테고리소싱] 컴포넌트 마운트 완료')
  },
  beforeDestroy() {
    // 메시지 리스너 제거
    window.removeEventListener('message', this.handleExtensionMessage)
  },
  methods: {
    // 카테고리그룹 초기화 (별도 IndexedDB에서 기존 그룹들 로드)
    async initializeCategoryGroups() {
      try {
        const dbName = 'categorySourcingGroups'
        const storeName = 'categoryGroups'

        // IndexedDB 열기
        const request = indexedDB.open(scopedDbName(dbName), 1)

        request.onupgradeneeded = (event) => {
          const db = event.target.result
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: 'groupName' })
          }
        }

        const db = await new Promise((resolve, reject) => {
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => reject(request.error)
        })

        // 기존 그룹들 로드
        const transaction = db.transaction([storeName], 'readonly')
        const store = transaction.objectStore(storeName)
        const getAllRequest = store.getAll()

        getAllRequest.onsuccess = () => {
          const groups = getAllRequest.result.map(item => item.groupName)
          if (groups.length > 0) {
            this.categoryGroups = ['Default', ...groups.filter(g => g !== 'Default')]
          }
          console.log('📂 [카테고리그룹] 초기화 완료:', this.categoryGroups)
        }

        db.close()
      } catch (error) {
        console.error('❌ [카테고리그룹] 초기화 실패:', error)
      }
    },

    // 카테고리 모달 열기
    openCategoryModal() {
      if (!this.categoryTree) {
        this.loadCategoryList()
      }
      this.showCategoryModal = true
    },

    // 새 카테고리그룹 생성
    async createCategoryGroup() {
      const groupName = prompt('새 카테고리그룹 이름을 입력하세요:')
      if (!groupName || groupName.trim() === '') return

      const trimmedName = groupName.trim()
      if (this.categoryGroups.includes(trimmedName)) {
        alert('이미 존재하는 그룹명입니다.')
        return
      }

      try {
        await this.saveCategoryGroupToDB(trimmedName)
        this.categoryGroups.push(trimmedName)
        this.activeGroup = trimmedName
        this.analysisResults = []
        console.log('✅ [카테고리그룹] 생성 완료:', trimmedName)
      } catch (error) {
        console.error('❌ [카테고리그룹] 생성 실패:', error)
        alert('카테고리그룹 생성에 실패했습니다.')
      }
    },

    // 카테고리그룹 전환
    async switchCategoryGroup(groupName) {
      this.activeGroup = groupName
      await this.loadFromIndexedDB()
      console.log('🔄 [카테고리그룹] 전환:', groupName)
    },

    // 카테고리그룹 삭제
    async deleteCategoryGroup(groupName) {
      if (groupName === 'Default') {
        alert('기본 그룹은 삭제할 수 없습니다.')
        return
      }

      if (!confirm(`'${groupName}' 그룹과 관련된 모든 데이터를 삭제하시겠습니까?`)) return

      try {
        // 그룹 데이터 삭제
        await this.deleteCategoryGroupFromDB(groupName)

        // 그룹 목록에서 제거
        this.categoryGroups = this.categoryGroups.filter(g => g !== groupName)

        // 현재 그룹이 삭제된 그룹이면 Default로 전환
        if (this.activeGroup === groupName) {
          this.activeGroup = 'Default'
          await this.loadFromIndexedDB()
        }

        console.log('🗑️ [카테고리그룹] 삭제 완료:', groupName)
      } catch (error) {
        console.error('❌ [카테고리그룹] 삭제 실패:', error)
        alert('카테고리그룹 삭제에 실패했습니다.')
      }
    },

    // 카테고리그룹을 DB에 저장
    async saveCategoryGroupToDB(groupName) {
      const dbName = 'categorySourcingGroups'
      const storeName = 'categoryGroups'

      const request = indexedDB.open(scopedDbName(dbName), 1)
      const db = await new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })

      const transaction = db.transaction([storeName], 'readwrite')
      const store = transaction.objectStore(storeName)

      await new Promise((resolve, reject) => {
        const addRequest = store.put({ groupName, createdAt: new Date() })
        addRequest.onsuccess = () => resolve()
        addRequest.onerror = () => reject(addRequest.error)
      })

      db.close()
    },

    // 카테고리그룹을 DB에서 삭제
    async deleteCategoryGroupFromDB(groupName) {
      // 1. 그룹 정보 삭제
      const groupDbName = 'categorySourcingGroups'
      const groupStoreName = 'categoryGroups'

      const groupRequest = indexedDB.open(scopedDbName(groupDbName), 1)
      const groupDb = await new Promise((resolve, reject) => {
        groupRequest.onsuccess = () => resolve(groupRequest.result)
        groupRequest.onerror = () => reject(groupRequest.error)
      })

      const groupTransaction = groupDb.transaction([groupStoreName], 'readwrite')
      const groupStore = groupTransaction.objectStore(groupStoreName)

      await new Promise((resolve, reject) => {
        const deleteRequest = groupStore.delete(groupName)
        deleteRequest.onsuccess = () => resolve()
        deleteRequest.onerror = () => reject(deleteRequest.error)
      })

      groupDb.close()

      // 2. 해당 그룹의 모든 데이터 삭제
      const dataRequest = indexedDB.open(scopedDbName(this.dbName), 1)
      const dataDb = await new Promise((resolve, reject) => {
        dataRequest.onsuccess = () => resolve(dataRequest.result)
        dataRequest.onerror = () => reject(dataRequest.error)
      })

      const dataTransaction = dataDb.transaction([this.storeName], 'readwrite')
      const dataStore = dataTransaction.objectStore(this.storeName)
      const index = dataStore.index('groupId')

      const getAllRequest = index.getAll(groupName)
      getAllRequest.onsuccess = () => {
        const items = getAllRequest.result
        items.forEach(item => {
          dataStore.delete(item.id)
        })
      }

      dataDb.close()
    },

    // IndexedDB 초기화
    async initializeDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(scopedDbName(this.dbName), 1)
        
        request.onerror = () => reject(request.error)
        request.onsuccess = () => {
          this.db = request.result
          resolve()
        }
        
        request.onupgradeneeded = (event) => {
          const db = event.target.result
          if (!db.objectStoreNames.contains(this.storeName)) {
            const store = db.createObjectStore(this.storeName, { keyPath: 'id' })
            store.createIndex('groupId', 'groupId', { unique: false })
            store.createIndex('categoryCode', 'categoryCode', { unique: false })
            store.createIndex('collectedAt', 'collectedAt', { unique: false })
          }
        }
      })
    },

    // 카테고리 리스트 로드
    loadCategoryList() {
      if (this.isLoadingCategories) return

      this.isLoadingCategories = true

      console.log('🔍 [카테고리소싱] 카테고리 리스트 요청')

      // 확장 프로그램에 카테고리 리스트 요청
      window.postMessage({
        greeting: 'getCategorySourcingList'
      }, '*')
    },

    // 카테고리 선택
    selectCategory(levelIndex, category) {
      console.log(`📂 [카테고리선택] ${levelIndex + 1}차:`, category.displayItemCategoryDto.name)

      // 현재 레벨에서 선택
      this.categoryLevels[levelIndex].selected = category.displayItemCategoryDto.displayItemCategoryCode

      // 하위 레벨들 초기화
      for (let i = levelIndex + 1; i < this.categoryLevels.length; i++) {
        this.categoryLevels[i].categories = []
        this.categoryLevels[i].selected = null
      }

      // 다음 레벨에 하위 카테고리 설정 (동적으로 확장)
      if (category.child && category.child.length > 0) {
        this.$set(this.categoryLevels, levelIndex + 1, {
          categories: category.child,
          selected: null
        })
      }

      // 선택된 카테고리 정보 업데이트
      this.updateSelectedCategory()
    },

    // 선택된 카테고리 정보 업데이트
    updateSelectedCategory() {
      const selectedPath = []
      let currentCategory = null

      for (let i = 0; i < this.categoryLevels.length; i++) {
        const level = this.categoryLevels[i]
        if (level.selected) {
          const category = level.categories.find(c => c.displayItemCategoryDto.displayItemCategoryCode === level.selected)
          if (category) {
            selectedPath.push(category.displayItemCategoryDto.name)
            currentCategory = category
          }
        } else {
          break
        }
      }

      if (selectedPath.length > 0) {
        this.selectedCategory = currentCategory
        this.selectedCategoryPath = selectedPath.join(' > ')
        this.leafCategoriesCount = this.countLeafCategories(currentCategory)
      } else {
        this.selectedCategory = null
        this.selectedCategoryPath = ''
        this.leafCategoriesCount = 0
      }
    },

    // Leaf 카테고리 개수 계산
    countLeafCategories(category) {
      if (!category.child || category.child.length === 0) {
        return 1
      }

      let count = 0
      for (const child of category.child) {
        count += this.countLeafCategories(child)
      }
      return count
    },

    // 데이터 수집 시작
    async startDataCollection() {
      if (!this.selectedCategory) {
        alert('카테고리를 선택해주세요.')
        return
      }

      // 모달 닫기
      this.showCategoryModal = false

      this.isLoading = true
      this.progressPercent = 0
      this.progressMessage = 'Leaf 카테고리를 찾는 중...'

      console.log('🚀 [데이터수집] 시작:', this.selectedCategoryPath)

      // Leaf 카테고리들 수집
      const leafCategories = this.collectLeafCategories(this.selectedCategory)
      console.log(`📊 [데이터수집] ${leafCategories.length}개 Leaf 카테고리 발견`)

      // 순차적으로 데이터 수집
      for (let i = 0; i < leafCategories.length; i++) {
        const category = leafCategories[i]
        this.progressPercent = (i / leafCategories.length) * 90 // 0%~90%
        this.progressMessage = `${category.displayItemCategoryDto.name} 데이터 수집 중... (${i + 1}/${leafCategories.length})`

        try {
          await this.collectCategoryData(category)

          // API 호출 간격 조절
          if (i < leafCategories.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        } catch (error) {
          console.error(`❌ [데이터수집] ${category.displayItemCategoryDto.name} 실패:`, error)
        }
      }

      this.progressPercent = 95
      this.progressMessage = '헤더 생성 중...'

      // 실제 수집된 데이터를 기반으로 헤더 재생성
      this.generateHeadersFromExistingData()

      this.progressPercent = 100
      this.progressMessage = '데이터 수집 완료!'

      // 결과 저장
      await this.saveToIndexedDB()

      setTimeout(() => {
        this.isLoading = false
      }, 1000)
    },

    // Leaf 카테고리들 수집
    collectLeafCategories(category) {
      const leafCategories = []

      const traverse = (cat, path = []) => {
        const currentPath = [...path, cat.displayItemCategoryDto.name]

        if (!cat.child || cat.child.length === 0) {
          // Leaf 카테고리 - 하위 경로만 저장 (선택된 카테고리 이후 경로)
          leafCategories.push({
            ...cat,
            categoryPath: currentPath
          })
        } else {
          // 하위 카테고리 탐색
          for (const child of cat.child) {
            traverse(child, currentPath)
          }
        }
      }

      // 선택된 카테고리부터 시작 (빈 path로 시작)
      traverse(category, [])
      return leafCategories
    },

    // 개별 카테고리 데이터 수집
    async collectCategoryData(category) {
      console.log(`📊 [개별수집] ${category.displayItemCategoryDto.name} 시작`)

      // 1. 카테고리 메트릭 수집
      const metrics = await this.getCategoryMetrics(category.displayItemCategoryDto.displayItemCategoryCode)

      // 2. TOP20 키워드 수집
      const keywords = await this.getCategoryKeywords(category.displayItemCategoryDto.displayItemCategoryCode)

      // 3. 데이터 결합
      const analysisData = this.combineAnalysisData(category, metrics, keywords)

      // 4. 기존 데이터 확인 후 업데이트 또는 추가
      const existingIndex = this.analysisResults.findIndex(item => item.id === analysisData.id)
      if (existingIndex !== -1) {
        // 기존 데이터 업데이트
        this.$set(this.analysisResults, existingIndex, analysisData)
        console.log(`🔄 [데이터업데이트] ${category.displayItemCategoryDto.name} 기존 데이터 업데이트`)
      } else {
        // 새 데이터 추가
        this.analysisResults.push(analysisData)
        console.log(`➕ [데이터추가] ${category.displayItemCategoryDto.name} 새 데이터 추가`)
      }

      console.log(`✅ [개별수집] ${category.displayItemCategoryDto.name} 완료`)
    },

    // 카테고리 메트릭 수집
    getCategoryMetrics(categoryCode) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          resolve(null) // 타임아웃 시 null 반환
        }, 15000)

        // 임시 Promise 저장
        this.metricsPromises = this.metricsPromises || {}
        this.metricsPromises[categoryCode] = { resolve, reject, timeout }

        // 확장 프로그램에 메트릭 요청
        window.postMessage({
          greeting: 'getCategorySourcingMetrics',
          categoryCode: categoryCode
        }, '*')
      })
    },

    // 카테고리 키워드 수집
    getCategoryKeywords(categoryCode) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          resolve([]) // 타임아웃 시 빈 배열 반환
        }, 15000)

        // 임시 Promise 저장
        this.keywordsPromises = this.keywordsPromises || {}
        this.keywordsPromises[categoryCode] = { resolve, reject, timeout }

        // 확장 프로그램에 키워드 요청
        window.postMessage({
          greeting: 'getCategorySourcingKeywords',
          categoryCode: categoryCode
        }, '*')
      })
    },

    // 분석 데이터 결합
    combineAnalysisData(category, metrics, keywords) {
      const data = {
        id: `${this.activeGroup}_${category.displayItemCategoryDto.displayItemCategoryCode}`,
        groupId: this.activeGroup,
        categoryCode: category.displayItemCategoryDto.displayItemCategoryCode,
        collectedAt: new Date()
      }

      // 선택된 카테고리 경로와 leaf 카테고리 경로 결합
      const selectedPath = this.selectedCategoryPath ? this.selectedCategoryPath.split(' > ') : []
      const leafPath = category.categoryPath || []

      // 전체 카테고리 경로 생성
      // leafPath는 선택된 카테고리부터 시작하므로 selectedPath의 마지막 요소와 leafPath의 첫 번째 요소가 같을 수 있음
      let fullCategoryPath = [...selectedPath]

      // leafPath에서 중복되지 않는 부분만 추가
      leafPath.forEach((pathName, index) => {
        if (index === 0 && selectedPath.length > 0 && selectedPath[selectedPath.length - 1] === pathName) {
          // 첫 번째 요소가 선택된 경로의 마지막과 같으면 건너뛰기 (중복 제거)
          return
        }
        fullCategoryPath.push(pathName)
      })

      // 카테고리 경로를 동적 컬럼으로 분리
      fullCategoryPath.forEach((pathName, index) => {
        data[`category${index + 1}`] = pathName
      })

      console.log(`📊 [데이터결합] 선택경로: [${selectedPath.join(' > ')}], Leaf경로: [${leafPath.join(' > ')}], 전체경로: [${fullCategoryPath.join(' > ')}]`)
      console.log(`📊 [데이터결합] 생성된 카테고리 컬럼:`, Object.keys(data).filter(key => key.startsWith('category')).sort())

      // 메트릭 데이터 추가
      if (metrics) {
        data.totalImpression = metrics.impression || 0
        data.glanceView = metrics.glanceView || 0
        data.ctr = metrics.impression > 0 ? ((metrics.glanceView / metrics.impression) * 100).toFixed(2) : 0
        data.top100TotalImpression = metrics.top100Impression || 0
        data.top100AdsImpression = metrics.top100AdsImpression || 0
        data.top100AdsImpressionRate = metrics.top100AdsImpressionPercentage || 0
        data.top100OrganicImpression = metrics.top100SearchImpression || 0
        data.top100OrganicImpressionRate = metrics.top100SearchImpressionPercentage || 0
      }

      // TOP20 키워드 통합 데이터 및 개별 데이터 저장
      if (keywords && keywords.length > 0) {
        const keywordList = keywords.slice(0, 20)

        // 통합 데이터 계산
        let totalImpression = 0
        let totalOrganicImpression = 0
        let totalAdsImpression = 0
        let totalSearchVolume = 0
        let totalSearchClick = 0

        // 개별 키워드 데이터 생성
        const keywordDetails = keywordList.map((keyword, index) => {
          const detail = keyword.keywordDetail || {}
          const metric = keyword.keywordMetric || {}

          totalImpression += metric.impression || 0
          totalOrganicImpression += metric.organicImpression || 0
          totalAdsImpression += metric.adImpression || 0
          totalSearchVolume += metric.searchVolume || 0
          totalSearchClick += metric.srpClick || 0

          return {
            rank: index + 1,
            keyword: detail.keyword || '',
            avgPrice: detail.avgPrice || 0,
            priceRange: detail.priceRangeStart && detail.priceRangeEnd ?
              `${detail.priceRangeStart}~${detail.priceRangeEnd}` : '',
            totalImpression: metric.impression || 0,
            organicImpression: metric.organicImpression || 0,
            adsImpression: metric.adImpression || 0,
            adsImpressionRate: metric.impression > 0 ?
              ((metric.adImpression / metric.impression) * 100).toFixed(2) : 0,
            searchVolume: metric.searchVolume || 0,
            searchClick: metric.srpClick || 0,
            clickRate: metric.organicImpression > 0 ?
              ((metric.srpClick / metric.organicImpression) * 100).toFixed(2) : 0
          }
        })

        // 통합 데이터 저장
        data.keywordsTotalImpression = totalImpression
        data.keywordsOrganicImpression = totalOrganicImpression
        data.keywordsAdsImpression = totalAdsImpression
        data.keywordsAdsImpressionRate = totalImpression > 0 ?
          ((totalAdsImpression / totalImpression) * 100).toFixed(2) : 0
        data.keywordsSearchVolume = totalSearchVolume
        data.keywordsSearchClick = totalSearchClick
        data.keywordsClickRate = totalOrganicImpression > 0 ?
          ((totalSearchClick / totalOrganicImpression) * 100).toFixed(2) : 0
        data.keywordsDetail = '상세보기'

        // 개별 키워드 데이터 저장 (모달용)
        data.keywordList = keywordDetails
        data.categoryPath = fullCategoryPath.join(' > ')

        // 엑셀 다운로드용 개별 키워드 데이터도 유지
        keywordList.forEach((keyword, index) => {
          const prefix = `top${index + 1}`
          const detail = keyword.keywordDetail || {}
          const metric = keyword.keywordMetric || {}

          data[`${prefix}Keyword`] = detail.keyword || ''
          data[`${prefix}KeywordAvgPrice`] = detail.avgPrice || 0
          data[`${prefix}KeywordPriceRange`] = detail.priceRangeStart && detail.priceRangeEnd ?
            `${detail.priceRangeStart}~${detail.priceRangeEnd}` : ''
          data[`${prefix}KeywordTotalImpression`] = metric.impression || 0
          data[`${prefix}KeywordOrganicImpression`] = metric.organicImpression || 0
          data[`${prefix}KeywordAdsImpression`] = metric.adImpression || 0
          data[`${prefix}KeywordAdsImpressionRate`] = metric.impression > 0 ?
            ((metric.adImpression / metric.impression) * 100).toFixed(2) : 0
          data[`${prefix}KeywordSearchVolume`] = metric.searchVolume || 0
          data[`${prefix}KeywordSearchClick`] = metric.srpClick || 0
          data[`${prefix}KeywordClickRate`] = metric.organicImpression > 0 ?
            ((metric.srpClick / metric.organicImpression) * 100).toFixed(2) : 0
        })
      }

      return data
    },

    // Extension 메시지 처리
    async handleExtensionMessage(event) {
      const message = event.data

      if (message.res === 'getCategorySourcingListComplete') {
        console.log('✅ [카테고리리스트] 수집 완료')
        this.processCategoryList(message.data)
        this.isLoading = false
      }

      if (message.res === 'getCategorySourcingMetricsComplete') {
        const categoryCode = message.categoryCode
        if (this.metricsPromises && this.metricsPromises[categoryCode]) {
          const promise = this.metricsPromises[categoryCode]
          clearTimeout(promise.timeout)
          promise.resolve(message.data)
          delete this.metricsPromises[categoryCode]
        }
      }

      if (message.res === 'getCategorySourcingKeywordsComplete') {
        const categoryCode = message.categoryCode
        if (this.keywordsPromises && this.keywordsPromises[categoryCode]) {
          const promise = this.keywordsPromises[categoryCode]
          clearTimeout(promise.timeout)
          promise.resolve(message.data)
          delete this.keywordsPromises[categoryCode]
        }
      }

      // 에러 처리
      if (message.res === 'getCategorySourcingListError') {
        console.error('❌ [카테고리리스트] 수집 실패:', message.error)
        alert('카테고리 리스트 수집에 실패했습니다.')
        this.isLoading = false
      }
    },

    // 카테고리 리스트 처리
    processCategoryList(data) {
      if (!data || !data.child) {
        console.error('❌ [카테고리리스트] 잘못된 데이터 형식')
        return
      }

      this.categoryTree = data

      // ROOT 하위 카테고리들 (1차 카테고리만 15개 제한)
      const rootCategories = data.child.slice(0, 15)

      this.categoryLevels = [
        {
          categories: rootCategories,
          selected: null
        }
      ]

      console.log(`📂 [카테고리리스트] ${rootCategories.length}개 1차 카테고리 로드`)

      // 카테고리 로딩 완료
      this.isLoadingCategories = false
    },

    // 테이블 헤더 초기화 (기본 헤더만)
    initializeTableHeaders() {
      this.tableHeaders = [
        // 메트릭 데이터
        { text: '총노출수', value: 'totalImpression', sortable: true },
        { text: '클릭수', value: 'glanceView', sortable: true },
        { text: 'CTR(%)', value: 'ctr', sortable: true },
        { text: 'TOP100총노출수', value: 'top100TotalImpression', sortable: true },
        { text: 'TOP100광고노출수', value: 'top100AdsImpression', sortable: true },
        { text: 'TOP100광고노출비율(%)', value: 'top100AdsImpressionRate', sortable: true },
        { text: 'TOP100자연노출수', value: 'top100OrganicImpression', sortable: true },
        { text: 'TOP100자연노출비율(%)', value: 'top100OrganicImpressionRate', sortable: true }
      ]

      // TOP20 키워드 통합 헤더 추가
      this.tableHeaders.push(
        { text: 'TOP20키워드 총노출수', value: 'keywordsTotalImpression', sortable: true },
        { text: 'TOP20키워드 자연노출수', value: 'keywordsOrganicImpression', sortable: true },
        { text: 'TOP20키워드 광고노출수', value: 'keywordsAdsImpression', sortable: true },
        { text: 'TOP20키워드 광고노출비율(%)', value: 'keywordsAdsImpressionRate', sortable: true },
        { text: 'TOP20키워드 검색수', value: 'keywordsSearchVolume', sortable: true },
        { text: 'TOP20키워드 검색클릭수', value: 'keywordsSearchClick', sortable: true },
        { text: 'TOP20키워드 클릭율(%)', value: 'keywordsClickRate', sortable: true },
        { text: 'TOP20키워드 상세보기', value: 'keywordsDetail', sortable: false, isSlot: true }
      )

      // 수집시간
      this.tableHeaders.push({ text: '수집시간', value: 'collectedAt', sortable: true })
    },

    // 선택된 카테고리 기반 동적 헤더 생성
    generateDynamicHeaders() {
      if (!this.selectedCategoryPath) return

      // 선택된 카테고리 경로를 분리
      const categoryNames = this.selectedCategoryPath.split(' > ')

      // 카테고리 헤더 생성 (1차카테고리, 2차카테고리 형태로 헤더 생성)
      const categoryHeaders = categoryNames.map((categoryName, index) => ({
        text: `${index + 1}차카테고리`,
        value: `category${index + 1}`,
        sortable: true
      }))

      // 기존 헤더에서 카테고리 헤더 제거 후 앞에 추가
      const nonCategoryHeaders = this.tableHeaders.filter(header =>
        !header.value.startsWith('category')
      )

      this.tableHeaders = [...categoryHeaders, ...nonCategoryHeaders]

      console.log(`📊 [테이블헤더] ${categoryHeaders.length}개 카테고리 컬럼 생성 (${categoryNames.join(' > ')})`)
    },

    // 기존 데이터에서 헤더 생성 (IndexedDB 로드 시 사용)
    generateHeadersFromExistingData() {
      if (this.analysisResults.length === 0) return

      // 모든 데이터에서 가장 깊은 카테고리 레벨 찾기 (동적으로 확장)
      let maxCategoryLevel = 0
      const categoryLevelCounts = {}

      this.analysisResults.forEach((item, itemIndex) => {
        const itemCategories = []
        // 최대 10차까지 확인 (필요시 더 확장 가능)
        for (let i = 1; i <= 10; i++) {
          const categoryKey = `category${i}`
          if (item[categoryKey] && item[categoryKey].trim() !== '') {
            maxCategoryLevel = Math.max(maxCategoryLevel, i)
            itemCategories.push(`${i}차:${item[categoryKey]}`)
            categoryLevelCounts[i] = (categoryLevelCounts[i] || 0) + 1
          }
        }
        if (itemIndex < 3) { // 처음 3개 아이템만 로그
          console.log(`📊 [헤더생성] 아이템 ${itemIndex + 1} 카테고리:`, itemCategories)
        }
      })

      console.log(`📊 [헤더생성] 카테고리 레벨별 개수:`, categoryLevelCounts)
      console.log(`📊 [헤더생성] 최대 카테고리 레벨: ${maxCategoryLevel}차`)

      // 찾은 최대 레벨까지 헤더 생성
      const categoryHeaders = []
      for (let i = 1; i <= maxCategoryLevel; i++) {
        categoryHeaders.push({
          text: `${i}차카테고리`,
          value: `category${i}`,
          sortable: true
        })
      }

      // 기존 헤더에서 카테고리 헤더 제거 후 앞에 추가
      const nonCategoryHeaders = this.tableHeaders.filter(header =>
        !header.value.startsWith('category')
      )

      this.tableHeaders = [...categoryHeaders, ...nonCategoryHeaders]

      console.log(`📊 [테이블헤더] 기존 데이터 기반 ${categoryHeaders.length}개 카테고리 컬럼 생성 (최대 ${maxCategoryLevel}차까지)`)
    },

    // 카테고리 레벨 수에 따른 컬럼 클래스 계산
    getColumnClass(levelCount) {
      if (levelCount <= 3) return 'col-md-4'
      if (levelCount <= 4) return 'col-md-3'
      if (levelCount <= 6) return 'col-md-2'
      return 'col-md-1' // 7차 이상일 경우
    },

    // 키워드 상세보기 모달 열기
    showKeywordDetails(item) {
      if (!item.keywordList || item.keywordList.length === 0) {
        alert('키워드 데이터가 없습니다.')
        return
      }

      this.selectedKeywordData = item
      this.keywordTableData = item.keywordList
      this.showKeywordModal = true

      console.log('📊 [키워드상세] 모달 열기:', item.categoryPath)
    },

    // 소싱관리 보드에 담기 (TOP20 키워드를 키워드 단위로 펼쳐서 INSERT)
    async addToSourcingBoard() {
      if (!this.selectedKeywordData || !this.selectedKeywordData.keywordList || this.selectedKeywordData.keywordList.length === 0) {
        alert('담을 키워드가 없습니다.')
        return
      }

      const keywordList = this.selectedKeywordData.keywordList
      const categoryPath = this.selectedKeywordData.categoryPath || ''

      if (!confirm(keywordList.length + '개의 키워드를 소싱관리에 담으시겠습니까?')) return

      const rows = keywordList.map(item => ({
        키워드: item.keyword || '',
        카테고리: categoryPath,
        상품명: '',
        카테고리_avgPrice: item.avgPrice,
        카테고리_priceRange: item.priceRange,
        카테고리_totalImpression: item.totalImpression,
        카테고리_organicImpression: item.organicImpression,
        카테고리_adsImpression: item.adsImpression,
        카테고리_adsImpressionRate: item.adsImpressionRate,
        카테고리_searchVolume: item.searchVolume,
        카테고리_searchClick: item.searchClick,
        카테고리_clickRate: item.clickRate,
        진도출처: '카테고리소싱분석'
      }))

      try {
        const { data } = await axios.post('/api/sourcing/board/save', rows)
        if (data && data.res === 'OK') {
          alert(rows.length + '개를 소싱관리에 담았습니다.')
        } else {
          alert((data && data.alert) || '소싱관리 담기에 실패했습니다.')
        }
      } catch (e) {
        console.error('소싱관리 담기 실패', e)
        alert('소싱관리 담기에 실패했습니다.')
      }
    },

    // 키워드 데이터 다운로드
    downloadKeywordData() {
      if (!this.selectedKeywordData || !this.selectedKeywordData.keywordList) {
        alert('다운로드할 키워드 데이터가 없습니다.')
        return
      }

      try {
        // 워크북 생성
        const wb = XLSX.utils.book_new()

        // 키워드 데이터를 엑셀 형태로 변환
        const keywordData = this.selectedKeywordData.keywordList.map(keyword => ({
          '순위': keyword.rank,
          '키워드': keyword.keyword,
          '평균가': keyword.avgPrice,
          '가격대': keyword.priceRange,
          '총노출수': keyword.totalImpression,
          '자연노출수': keyword.organicImpression,
          '광고노출수': keyword.adsImpression,
          '광고노출비율(%)': keyword.adsImpressionRate,
          '검색수': keyword.searchVolume,
          '검색클릭수': keyword.searchClick,
          '클릭율(%)': keyword.clickRate
        }))

        // 워크시트 생성
        const ws = XLSX.utils.json_to_sheet(keywordData)
        XLSX.utils.book_append_sheet(wb, ws, 'TOP20키워드')

        // 파일명 생성
        const categoryName = this.selectedKeywordData.categoryPath.replace(/[^\w\s가-힣]/g, '_')
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')
        const filename = `키워드상세_${categoryName}_${timestamp}.xlsx`

        // 다운로드
        XLSX.writeFile(wb, filename)

        console.log('📊 [키워드다운로드] 완료:', filename)
      } catch (error) {
        console.error('❌ [키워드다운로드] 실패:', error)
        alert('키워드 데이터 다운로드에 실패했습니다.')
      }
    },

    // IndexedDB에서 데이터 로드
    async loadFromIndexedDB() {
      if (!this.db) return

      try {
        const transaction = this.db.transaction([this.storeName], 'readonly')
        const store = transaction.objectStore('categorySourcingData')
        const index = store.index('groupId')
        const request = index.getAll(this.activeGroup)

        request.onsuccess = () => {
          this.analysisResults = request.result || []
          console.log(`📂 [IndexedDB] ${this.analysisResults.length}개 데이터 로드`)

          // 데이터가 있으면 기존 데이터 기반으로 헤더 생성
          if (this.analysisResults.length > 0) {
            this.generateHeadersFromExistingData()
          }
        }

        request.onerror = () => {
          console.error('❌ [IndexedDB] 데이터 로드 실패:', request.error)
        }
      } catch (error) {
        console.error('❌ [IndexedDB] 로드 에러:', error)
      }
    },

    // IndexedDB에 데이터 저장
    async saveToIndexedDB() {
      if (!this.db || this.analysisResults.length === 0) return

      try {
        const transaction = this.db.transaction([this.storeName], 'readwrite')
        const store = transaction.objectStore('categorySourcingData')

        for (const result of this.analysisResults) {
          await new Promise((resolve, reject) => {
            const request = store.put(result)
            request.onsuccess = () => resolve()
            request.onerror = () => reject(request.error)
          })
        }

        console.log(`💾 [IndexedDB] ${this.analysisResults.length}개 데이터 저장 완료`)
      } catch (error) {
        console.error('❌ [IndexedDB] 저장 에러:', error)
      }
    },

    // 데이터 초기화
    async clearData() {
      if (!confirm('현재 그룹의 모든 데이터를 삭제하시겠습니까?')) return

      try {
        if (this.db) {
          const transaction = this.db.transaction([this.storeName], 'readwrite')
          const store = transaction.objectStore('categorySourcingData')
          const index = store.index('groupId')
          const request = index.openCursor(IDBKeyRange.only(this.activeGroup))

          request.onsuccess = (event) => {
            const cursor = event.target.result
            if (cursor) {
              cursor.delete()
              cursor.continue()
            }
          }
        }

        this.analysisResults = []
        this.categoryTree = null
        this.categoryLevels = []
        this.selectedCategory = null
        this.selectedCategoryPath = ''
        this.leafCategoriesCount = 0

        console.log('🗑️ [데이터초기화] 완료')
      } catch (error) {
        console.error('❌ [데이터초기화] 에러:', error)
      }
    },

    // 엑셀 다운로드 (Sheet1: 전체통계 / Sheet2: TOP20키워드 세로 펼침)
    downloadExcel() {
      if (this.analysisResults.length === 0) {
        alert('다운로드할 데이터가 없습니다.')
        return
      }

      try {
        // 전체 데이터에서 최대 카테고리 레벨 찾기 (테이블 헤더 생성과 동일한 방식)
        let maxCategoryLevel = 0
        this.analysisResults.forEach(item => {
          for (let i = 1; i <= 10; i++) {
            const categoryKey = `category${i}`
            if (item[categoryKey] && item[categoryKey].trim() !== '') {
              maxCategoryLevel = Math.max(maxCategoryLevel, i)
            }
          }
        })

        // 카테고리 컬럼을 row에 채우는 헬퍼
        const fillCategoryColumns = (row, item) => {
          for (let i = 1; i <= maxCategoryLevel; i++) {
            row[`${i}차카테고리`] = item[`category${i}`] || ''
          }
        }

        // Sheet1: 전체통계 (카테고리당 1행)
        const summaryData = this.analysisResults.map(item => {
          const row = {}
          fillCategoryColumns(row, item)
          row['총노출수'] = item.totalImpression || 0
          row['클릭수'] = item.glanceView || 0
          row['CTR(%)'] = item.ctr || 0
          row['TOP100총노출수'] = item.top100TotalImpression || 0
          row['TOP100광고노출수'] = item.top100AdsImpression || 0
          row['TOP100광고노출비율(%)'] = item.top100AdsImpressionRate || 0
          row['TOP100자연노출수'] = item.top100OrganicImpression || 0
          row['TOP100자연노출비율(%)'] = item.top100OrganicImpressionRate || 0
          row['수집시간'] = item.collectedAt ? new Date(item.collectedAt).toLocaleString() : ''
          return row
        })

        // Sheet2: TOP20키워드 (카테고리당 최대 20행으로 세로 펼침, 빈 키워드는 skip)
        const keywordData = []
        this.analysisResults.forEach(item => {
          for (let i = 1; i <= 20; i++) {
            const prefix = `top${i}`
            const keyword = item[`${prefix}Keyword`]
            if (!keyword) continue

            const row = {}
            fillCategoryColumns(row, item)
            row['순위'] = i
            row['키워드'] = keyword
            row['평균가'] = item[`${prefix}KeywordAvgPrice`] || 0
            row['가격대'] = item[`${prefix}KeywordPriceRange`] || ''
            row['총노출수'] = item[`${prefix}KeywordTotalImpression`] || 0
            row['자연노출수'] = item[`${prefix}KeywordOrganicImpression`] || 0
            row['광고노출수'] = item[`${prefix}KeywordAdsImpression`] || 0
            row['광고노출비율(%)'] = item[`${prefix}KeywordAdsImpressionRate`] || 0
            row['검색수'] = item[`${prefix}KeywordSearchVolume`] || 0
            row['검색클릭수'] = item[`${prefix}KeywordSearchClick`] || 0
            row['클릭율(%)'] = item[`${prefix}KeywordClickRate`] || 0
            keywordData.push(row)
          }
        })

        // 워크북 생성
        const wb = XLSX.utils.book_new()

        const setColWidths = (ws, sample) => {
          if (!sample) return
          ws['!cols'] = Object.keys(sample).map(key => ({
            wch: Math.max(key.length, 12)
          }))
        }

        const wsSummary = XLSX.utils.json_to_sheet(summaryData)
        setColWidths(wsSummary, summaryData[0])
        XLSX.utils.book_append_sheet(wb, wsSummary, '전체통계')

        const wsKeyword = XLSX.utils.json_to_sheet(keywordData)
        setColWidths(wsKeyword, keywordData[0])
        XLSX.utils.book_append_sheet(wb, wsKeyword, 'TOP20키워드')

        // 파일명 생성 + 다운로드
        const fileName = `카테고리소싱분석_${this.activeGroup}_${new Date().toISOString().slice(0, 10)}.xlsx`
        XLSX.writeFile(wb, fileName)

        console.log('📥 [엑셀다운로드] 완료:', fileName, `(전체통계 ${summaryData.length}행 / 키워드 ${keywordData.length}행)`)
      } catch (error) {
        console.error('❌ [엑셀다운로드] 에러:', error)
        alert('엑셀 다운로드 중 오류가 발생했습니다.')
      }
    },

    // 전체 엑셀 헤더 생성 (참고용, 실제로는 동적 생성)
    generateFullExcelHeaders() {
      const headers = []

      // 카테고리 헤더들은 동적으로 생성됨
      // 메트릭 헤더들
      headers.push('총노출수', '클릭수', 'CTR(%)', 'TOP100총노출수', 'TOP100광고노출수', 'TOP100광고노출비율(%)', 'TOP100자연노출수', 'TOP100자연노출비율(%)')

      // TOP20 키워드 헤더들
      for (let i = 1; i <= 20; i++) {
        headers.push(
          `TOP${i}키워드`, `TOP${i}평균가`, `TOP${i}가격대`, `TOP${i}총노출수`,
          `TOP${i}자연노출수`, `TOP${i}광고노출수`, `TOP${i}광고노출비율(%)`,
          `TOP${i}검색수`, `TOP${i}검색클릭수`, `TOP${i}클릭율(%)`
        )
      }

      headers.push('수집시간')
      return headers
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.list-group-item.active {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
}

.list-group-item-action:hover {
  background-color: #f8f9fa;
}

.text-truncate {
  max-width: 200px;
}

.card-header h6 {
  color: #495057;
  font-weight: 600;
}

.badge-pill {
  font-size: 0.75em;
}
</style>
